<template>
  <VModal :showing="showing" max-content="450px" container-classes="max-w-2xl" @update:showing="cancel">
    <template #header>
      <h2 class="mb-2 text-xl">Secure Access</h2>
      <p class="mt-2 mb-0 text-sm font-light text-gray-800">
        Define who has access to edit and view this Deck. To give View Only access to a new user, first add the user by entering their email
        below, and then grant access once the user has been added.
      </p>
    </template>

    <template #default>
      <form class="mb-4 text-md" data-testid="secure-access-add-non-admin" @submit.prevent="addNonAdmin">
        <FormField
          v-model="newUserEmail"
          :v-bind="newUserEmailAttrs"
          name="newUserEmail"
          type="email"
          placeholder="Enter an email to add a new user"
          autocomplete="off"
          :error="errors.newUserEmail"
        >
          <template #button>
            <VButton class="m-2 whitespace-nowrap" type="submit" variant="copper" size="sm" :disabled="!newUserEmail"> Add User </VButton>
          </template>
        </FormField>
      </form>

      <div v-if="loading" class="flex items-center justify-center w-full text-gray-500 h-80 opacity-40">
        <CircleSpinner class="h-5 w-5" />
      </div>

      <div v-else class="flex flex-col space-y-6">
        <h3 class="px-6 py-1 text-sm text-gray-700 rounded-md bg-gray-50">Admins</h3>
        <div v-for="user in admins" :id="`user-${user.id}`" :key="user.id" class="flex items-center pb-4 space-x-4">
          <img v-if="user.profile_image" class="ml-auto border border-gray-200 rounded-full h-9 w-9" :src="user.profile_image" />
          <div v-else class="flex items-center content-center justify-center ml-auto bg-gray-900 rounded-full w-9 h-9">
            <div class="font-medium text-center text-white">{{ user.initials }}</div>
          </div>
          <div class="flex-1">
            <p class="m-0 text-base text-gray-900">{{ user.name }}</p>
            <p class="m-0 text-sm text-gray-700">{{ user.email_address }}</p>
          </div>
          <div class="flex items-center justify-end flex-1">
            <VDropdown
              :model-value="user.has_edit_access ? VIEW_EDIT_OPTIONS[1] : VIEW_EDIT_OPTIONS[0]"
              :options="VIEW_EDIT_OPTIONS"
              @update:model-value="toggleEditAccess(user, $event)"
            >
              <template #current-single="{ item }">{{ item.name }}</template>
              <template #option="{ item }">
                <div class="px-3 py-2 text-sm hoverbg-gray-200 transition-color duration-150">{{ item.name }}</div>
              </template>
            </VDropdown>
          </div>
        </div>

        <div v-if="nonAdmins.length" class="space-y-6">
          <h3 class="px-6 py-1 text-sm text-gray-700 rounded-md bg-gray-50">Non-Admins</h3>
          <div v-for="user in nonAdmins" :id="`user-${user.id}`" :key="user.id" class="flex items-center pb-4 space-x-4">
            <img v-if="user.profile_image" :src="user.profile_image" class="ml-auto border border-gray-200 rounded-full h-9 w-9" />
            <div v-else class="flex items-center content-center justify-center ml-auto bg-gray-900 rounded-full w-9 h-9">
              <div class="font-medium text-center text-white">{{ user.initials }}</div>
            </div>
            <div class="flex-1">
              <p v-if="user.has_view_access" class="m-0 text-base text-gray-900">Invite sent to</p>
              <p class="m-0 text-sm text-gray-700">{{ user.email_address }}</p>
            </div>
            <div class="flex items-center justify-end flex-1">
              <VDropdown
                v-if="user.has_view_access"
                :model-value="VIEW_ONLY_OPTIONS[1]"
                :options="VIEW_ONLY_OPTIONS"
                @update:model-value="revokeViewAccess(user)"
              >
                <template #current-single>View Only</template>
                <template #option="{ item }">
                  <div class="px-3 py-2 text-sm hover_bg-gray-200 transition-color duration-150">{{ item.name }}</div>
                </template>
              </VDropdown>
              <VButton v-else @click="grantViewAccess(user)">Give View Only Access</VButton>
            </div>
          </div>
        </div>
        <div class="space-y-6">
          <h3 class="px-6 py-1 text-sm text-gray-700 rounded-md bg-gray-50">VRIFY Team Access</h3>
          <div class="flex items-center space-x-4">
            <VCircleLogo class="text-gray-900 w-9 h-9" />
            <div class="flex-1">
              <p class="m-0 text-base text-gray-900">VRIFY Team Access</p>
              <p class="m-0 text-sm text-gray-700">Allow access to view and edit</p>
            </div>
            <div class="flex items-center justify-end flex-1">
              <VSwitch :value="deck.allow_internal_access" @input="toggleInternalAccess" />
            </div>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="flex flex-row justify-between w-full gap-4">
        <VButton @click="$emit('goto:secure-links')">Share Secure Link</VButton>
        <VButton variant="copper" @click="cancel">Done</VButton>
      </div>
    </template>
  </VModal>
</template>

<script lang="ts" setup>
import type { PropType, DeepReadonly, Ref } from 'vue'
import { watch, toRefs, nextTick, computed } from 'vue'
import { z } from 'zod'
import { useForm } from 'vee-validate'
import ZodMessages from '@/zod_presets/messages'
import { toTypedSchema } from '@vee-validate/zod'

import type { Option } from '@/components/VDropdown.vue'
import type { EditDeck } from '~/composables/useEditDeck'
import VCircleLogo from '@/components/icons/VCircleLogo.svg'

type UnwrapRefArray<T> = T extends Ref<Array<infer S>> ? S : never

const VIEW_EDIT_OPTIONS = [
  { id: 1, name: 'No Access', value: false },
  { id: 2, name: 'View & Edit', value: true },
]
const VIEW_ONLY_OPTIONS = [
  { id: 1, name: 'No Access', value: false },
  { id: 2, name: 'View Only', value: true },
]

const { $sentry } = useNuxtApp()

const props = defineProps({
  deck: {
    type: Object as PropType<DeepReadonly<EditDeck>>,
    required: true,
  },
  showing: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits([
  'update:deck',
  'close',
  'error',
  'user-created',
  'goto:secure-links',
  'goto:remove-self',
  'update:deckInternalAccess',
])

const { state: userState } = useUser()

const { showing } = toRefs(props)
const { admins, nonAdmins, loading, grantEditAccess, revokeEditAccess, grantViewAccess, revokeViewAccess, addNewUser } =
  useEditSecureAccess({
    deck: computed(() => (props.showing ? props.deck : null)),
    onError(error: string) {
      emit('error', error)
      $sentry?.captureException(error)
    },
  })

const Schema = z.object({
  newUserEmail: z.string().email(ZodMessages.email).optional().or(z.literal('')),
})

const { handleSubmit, resetForm, defineField, errors } = useForm<z.infer<typeof Schema>>({
  validationSchema: toTypedSchema(Schema),
})

const [newUserEmail, newUserEmailAttrs] = defineField('newUserEmail')

watch(showing, showing => {
  if (!showing) return

  resetForm()
})

function cancel(): void {
  emit('close')
}

function _scrollUserIntoView(userId: number): void {
  document.getElementById(`user-${userId}`)?.scrollIntoView({ block: 'center' })
}

async function toggleEditAccess(admin: UnwrapRefArray<typeof admins>, { value: selectedOption }: { value: boolean }): Promise<void> {
  // show a confirmation modal if you try to revoke yourself.
  if (!selectedOption && admin.id === userState.value.user?.id) {
    emit('goto:remove-self')
    return
  }

  if (selectedOption && !admin.has_edit_access) {
    await grantEditAccess(admin)
  } else if (!selectedOption && admin.has_edit_access) {
    await revokeEditAccess(admin)
  }
}

async function toggleInternalAccess(value: boolean): Promise<void> {
  emit('update:deckInternalAccess', value)
}

const addNonAdmin = handleSubmit(async () => {
  const existingUser = admins.value.concat(nonAdmins.value).find(({ email_address }) => email_address === newUserEmail.value)
  if (existingUser) {
    await nextTick()
    _scrollUserIntoView(existingUser.id)
    return
  }

  const newUser = await addNewUser(newUserEmail.value!)
  if (!newUser) return

  emit('user-created', newUserEmail.value)
  resetForm()
  await nextTick()
  _scrollUserIntoView(newUser.user_id)
})
</script>

<style lang="stylus" scoped>
.error {
  @apply text-red-800
}

.form-field.gold :deep(.mx-input) {
  @apply border-gold-800 hover_border-gold-800
}
</style>
